import {
  AdditiveBlending,
  BackSide,
  Mesh,
  ShaderMaterial,
  SphereGeometry,
} from "three";

// Procedural Milky Way backdrop: a huge sphere rendered BackSide with a
// fragment shader that paints a noise-modulated band along a tilted
// great circle. No textures — gives a subtle, non-repeating cosmic
// atmosphere that motion through OrbitControls doesn't betray.

const MILKY_RADIUS = 85;

export function createMilkyWay(): Mesh {
  const geometry = new SphereGeometry(MILKY_RADIUS, 64, 48);

  const material = new ShaderMaterial({
    vertexShader: /* glsl */ `
      varying vec3 vDir;
      void main() {
        vDir = normalize(position);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: /* glsl */ `
      precision highp float;
      varying vec3 vDir;

      // Cheap hash-based value noise in 3D. Good enough for star clouds.
      float hash(vec3 p) {
        p = fract(p * vec3(443.897, 441.423, 437.195));
        p += dot(p, p.yxz + 19.19);
        return fract((p.x + p.y) * p.z);
      }

      float vnoise(vec3 p) {
        vec3 i = floor(p);
        vec3 f = fract(p);
        f = f * f * (3.0 - 2.0 * f);
        return mix(
          mix(mix(hash(i),                         hash(i + vec3(1,0,0)), f.x),
              mix(hash(i + vec3(0,1,0)),           hash(i + vec3(1,1,0)), f.x), f.y),
          mix(mix(hash(i + vec3(0,0,1)),           hash(i + vec3(1,0,1)), f.x),
              mix(hash(i + vec3(0,1,1)),           hash(i + vec3(1,1,1)), f.x), f.y),
          f.z
        );
      }

      float fbm(vec3 p) {
        float v = 0.0;
        float a = 0.5;
        for (int i = 0; i < 5; i++) {
          v += a * vnoise(p);
          p *= 2.12;
          a *= 0.52;
        }
        return v;
      }

      void main() {
        // Tilted band ~63° from the equator of our scene — a pleasing diagonal.
        vec3 bandNormal = normalize(vec3(0.42, 0.86, 0.28));
        float d = abs(dot(vDir, bandNormal));

        // Core band with soft falloff
        float band = 1.0 - smoothstep(0.0, 0.42, d);
        band = pow(band, 1.6);

        // Cloud structure
        vec3 p = vDir * 4.0;
        float clouds = fbm(p * 1.8) * 0.6 + fbm(p * 3.7 + vec3(5.2, 1.3, 9.8)) * 0.4;

        // Dark dust lanes — areas of lower intensity cutting across the band
        float dust = fbm(vDir * 11.0 + vec3(7.7, 2.1, 4.4));
        float dustMask = smoothstep(0.32, 0.72, dust);

        float intensity = band * clouds * mix(0.25, 1.0, dustMask) * 0.68;

        // Colour: warm star-cloud cream crossed with cool interstellar blue
        float tintMix = fbm(vDir * 2.2);
        vec3 warm = vec3(0.62, 0.50, 0.36);
        vec3 cool = vec3(0.32, 0.46, 0.72);
        vec3 tint = mix(warm, cool, tintMix);

        // A handful of "bright knot" regions — globular clusters, nebulae
        float knots = pow(fbm(vDir * 6.5 + vec3(2.3, 9.1, 5.0)), 5.0) * band;
        tint += vec3(0.95, 0.78, 0.55) * knots * 2.4;

        // Distant pink/violet hydrogen-alpha wash along the densest parts
        float ha = pow(band, 2.5) * smoothstep(0.45, 0.92, clouds);
        tint += vec3(0.45, 0.18, 0.38) * ha * 0.3;

        gl_FragColor = vec4(tint * intensity, intensity);
      }
    `,
    side: BackSide,
    transparent: true,
    depthWrite: false,
    depthTest: false,
    blending: AdditiveBlending,
  });

  const mesh = new Mesh(geometry, material);
  mesh.renderOrder = -2;
  mesh.frustumCulled = false;
  return mesh;
}
